import { domStorage } from '../_globals'

/*
  Bouton (+) flottant mobile — maquette Marine (kOUUJahZqtn1eymQkfMlBj)

  SEUIL D'APPARITION — c'est le seul point de la maquette qui ne se lit pas
  directement. Il est donc DÉDUIT et doit être confirmé par Marine.

  Ce que la maquette dit vraiment (frame 4093:2159, 390x8769, viewport 390x844) :
    · 4093:2837 « Primary Button » — le bouton lui-même : 64x64, à x=299 / y=945,
      fond #fff, ombre 0 5.6 33.4 rgba(0,0,0,.25), icône « add » 33.4.
    · 4093:2843 « Frame 182 » — sa frame parente : 54 x 813, à x=304 / y=191.
      Elle est plus ÉTROITE que le bouton qu'elle contient (54 < 64) : ce n'est
      donc pas le bouton, c'est la piste sur laquelle il reste visible.

  Trois lectures étaient possibles (101 / 165 / 844 px). La position verticale
  d'un élément FIXE dans une maquette longue indique la ZONE où il est visible,
  pas la distance de scroll qui le déclenche — d'où l'ambiguïté. La version
  précédente de ce fichier lisait le y=191 de la piste comme un seuil : c'est
  cette confusion-là qui est corrigée ici.

  Amélie a tranché sur le résultat attendu : « il ne doit pas s'afficher dès le
  premier écran ». Un seuil de 101 px le ferait apparaître alors que le hero est
  encore à l'écran : NON CONFORME. Le seuil retenu est donc la FIN DU PREMIER
  ÉCRAN, exprimée en 100vh et non en 844 px en dur, pour tenir sur tous les
  mobiles (les 844 px du hero de la maquette sont exactement la hauteur du
  viewport iPhone de référence).
*/

export default class ScrollCta {
  constructor() {
    if (!domStorage.scrollCta) return

    this.DOM = { el: domStorage.scrollCta }
    this.link = this.DOM.el.querySelector('.ScrollCta__btn')
    this.visible = null
    this.ticking = false
    // Mode ANCRÉ puis FIXE — c'est le STICKY_SCROLLS de la maquette enfin
    // implémenté fidèlement (retour Sandro passe 4) : au chargement le bouton
    // est ANCRÉ dans la page à hauteur du bouton de la carte Antilles
    // (Marine : + à y945 pour une carte finissant à 1030 -> bas de carte - 85),
    // donc SOUS le premier écran (conforme Amélie). Au scroll, quand sa
    // position atteint bottom:26 du viewport, il se fige (fixed).
    this.carte = document.querySelector('.BlockCta')

    this.onScroll = this.onScroll.bind(this)
    this.onResize = this.onResize.bind(this)
    this.update = this.update.bind(this)
    this.recale = this.recale.bind(this)

    // BUG CORRIGÉ (nuit 29/07) : l'ancre était calculée UNE SEULE FOIS ici,
    // avant que les images n'aient poussé/réduit le layout, et n'était
    // recalée qu'au resize. Mesuré sur https://sante.aio.wokine.dev/ :
    //   AVANT — --cta-top figé à 1110px aux largeurs 414 et 430,
    //           alors que le bas réel de .BlockCta - 85 vaut 894 (414)
    //           et 880 (430) : le (+) ancré tombait ~130px SOUS la carte,
    //           en plein sur le texte de la section suivante.
    //   APRÈS — l'ancre suit le layout : recalage à chaque changement de
    //           taille du body (ResizeObserver) et au window load.
    this.recale()
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(this.recale)
      this.ro.observe(document.body)
    } else {
      window.addEventListener('load', this.recale)
    }

    window.addEventListener('scroll', this.onScroll, { passive: true })
    // Rotation, ou barre d'URL mobile qui se rétracte : 100vh change,
    // le seuil doit suivre plutôt que rester figé sur la valeur de départ.
    window.addEventListener('resize', this.onResize, { passive: true })

    // 29/07 — RETOUR SANDRO : « quand on clique sur le (+) ça revient tout en
    // haut de la page ». Cause : le champ ACF page_hero.btn_offers n'est pas
    // renseigné, Twig rend donc href="#" (le piège connu : '#' est TRUTHY),
    // et un lien vers '#' remonte au sommet du document par définition.
    // Tant que le champ n'est pas rempli au back-office, le bouton amène au
    // bloc des offres de la page — c'est le sens du CTA replié « Découvrir nos
    // offres » — au lieu de renvoyer en haut. Dès que l'URL est saisie, ce
    // repli ne s'applique plus (href réel, comportement normal du lien).
    if (this.link) {
      const href = this.link.getAttribute('href')
      if (!href || href === '#') {
        this.link.addEventListener('click', (e) => {
          e.preventDefault()
          const cible = document.querySelector('.BlockOffersSlider, .BlockOffersType, .BlockProfilesOffers')
          if (!cible) return
          const y = cible.getBoundingClientRect().top + window.scrollY - 20
          if (window.lenis) window.lenis.scrollTo(y)
          else window.scrollTo({ top: y, behavior: 'smooth' })
        })
      }
    }

    // État initial (rechargement en cours de page, arrivée sur une ancre…)
    this.update()
  }

  onScroll() {
    if (this.ticking) return
    this.ticking = true
    window.requestAnimationFrame(this.update)
  }

  onResize() {
    this.recale()
  }

  // Recalcule l'ancre (bas de la carte - 85, cf. maquette Marine y945 pour une
  // carte finissant à 1030) et le seuil de bascule fixe. Appelé au démarrage,
  // au resize, et à CHAQUE variation de hauteur du body (images/lazy-load) —
  // c'est ce dernier cas qui manquait et laissait l'ancre périmée.
  recale() {
    if (this.carte) {
      const r = this.carte.getBoundingClientRect()
      const top = Math.round(r.bottom + window.scrollY) - 85
      if (top !== this.topAncre) {
        this.topAncre = top
        this.DOM.el.style.setProperty('--cta-top', this.topAncre + 'px')
      }
      this.seuil = Math.max(0, this.topAncre - (window.innerHeight - 26 - 64))
    } else {
      this.seuil = window.innerHeight
    }
    this.update()
  }

  update() {
    this.ticking = false

    const franchi = window.scrollY >= this.seuil
    // La zone de fin de page (bandeau d'avis eKomi, footer) est détectée par
    // need-help.js — présent sur TOUTES les pages, quand le (+) de Marine ne
    // vit que sur celles qui ont une .BlockCta — qui pose cette classe body.
    // Retour Sandro 29/07 : aucun bouton flottant ne recouvre ce contenu.
    const finDePage = document.body.classList.contains('--cta-fin-de-page')
    const shouldShow = franchi && !finDePage
    // ancré tant que le point de fixation n'est pas atteint (et qu'une carte existe)
    this.DOM.el.classList.toggle('--docked', !!this.carte && !franchi)
    if (shouldShow === this.visible) return

    this.visible = shouldShow
    this.DOM.el.classList.toggle('--visible', shouldShow)
    // en mode ancré le bouton est visible dans la page : accessible aussi
    const percevable = shouldShow || (!!this.carte && !shouldShow)
    this.DOM.el.setAttribute('aria-hidden', percevable ? 'false' : 'true')

    // Le bouton « Besoin d'aide » occupe le même coin (60x60 à 32/32) : quand
    // le (+) est visible, cette classe le fait remonter au-dessus
    // (_scroll-cta.scss) — les deux restent cliquables.
    document.body.classList.toggle('--scroll-cta-visible', shouldShow)

    // Un bouton invisible ne doit pas être atteignable au clavier
    if (this.link) this.link.setAttribute('tabindex', percevable ? '0' : '-1')
  }

  destroy() {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onResize)
    if (this.ro) this.ro.disconnect()
    else window.removeEventListener('load', this.recale)
  }
}
